import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FILE_TYPES, DISTRIBUTIONS, STATUS_METHODS, LINK_METHODS } from './constants';

function excelColumns(limit = 2) {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const cols = [];
    const gen = (prefix, depth) => {
        for (const l of letters) {
            const c = prefix + l;
            cols.push(c);
            if (depth < limit) gen(c, depth + 1);
        }
    };
    gen('', 1);
    return cols;
}

const COLUMN_POOL = excelColumns();

function CommaMini({ label, value, onChange, placeholder }) {
    return (
        <div>
            <label className="form-label text-white-50">{label}</label>
            <input
                type="text"
                className="form-control bg-dark text-white border-secondary py-2"
                value={(value || []).join(', ')}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
            />
        </div>
    );
}

function ColumnSelect({ columns, value, onChange, placeholder = 'Seleccionar…' }) {
    return (
        <select
            className="form-select bg-dark text-white border-secondary py-2"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={!columns.length}
        >
            <option value="">{columns.length ? placeholder : 'Añade columnas primero'}</option>
            {columns.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
    );
}

function ColumnasAdder({ columns, onChange }) {
    const [pending, setPending] = useState('');
    const available = COLUMN_POOL.filter((c) => !columns.includes(c));
    const add = () => {
        if (!pending) return;
        onChange([...columns, pending]);
        setPending('');
    };
    return (
        <div>
            <label className="form-label text-white-50">Columnas del reporte</label>
            <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                {columns.length === 0 && <span className="text-white-50 small">Sin columnas añadidas</span>}
                {columns.map((c) => (
                    <span key={c} className="badge bg-info bg-opacity-25 text-info d-inline-flex align-items-center gap-2">
                        {c}
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            style={{ fontSize: '0.8rem' }}
                            onClick={() => onChange(columns.filter((x) => x !== c))}
                            title="Quitar columna"
                        />
                    </span>
                ))}
                {columns.length > 0 && (
                    <span className="text-white-50 small">{columns.length} columna(s)</span>
                )}
            </div>
            <div className="d-flex gap-2">
                <select
                    className="form-select bg-dark text-white border-secondary py-2 w-auto"
                    value={pending}
                    onChange={(e) => setPending(e.target.value)}
                >
                    <option value="">Seleccionar columna…</option>
                    {available.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={add} disabled={!pending}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Añadir
                </button>
            </div>
        </div>
    );
}

function buildCfg(initial) {
    const base = (initial && initial.config_report) || {};
    return {
        file_type: base.file_type || 'xlsx',
        init_row: base.init_row !== undefined ? base.init_row : 1,
        columns: base.columns || [],
        identifiers: (base.identifiers || []).map((r) => ({ column: r.column || '', id: r.id || '' })),
        status: {
            column: (base.status && base.status.column) || '',
            method: (base.status && base.status.method) || 'equal',
            on: (base.status && base.status.on) || [],
            off: (base.status && base.status.off) || [],
        },
        link: base.link || {},
        metadata: {
            title: { column: (base.metadata && base.metadata.title && base.metadata.title.column) || '' },
            imprint: { column: (base.metadata && base.metadata.imprint && base.metadata.imprint.column) || '' },
        },
        distribution: base.distribution || 'stock',
    };
}

export function ReportsChannelForm({ initial, onSubmit, saving, onCancel }) {
    const [cfg, setCfg] = useState(() => buildCfg(initial));

    useEffect(() => {
        setCfg(buildCfg(initial));
    }, [initial]);

    const set = (field, value) => setCfg((prev) => ({ ...prev, [field]: value }));

    const setIdentRow = (i, field, val) => {
        const next = [...cfg.identifiers];
        next[i] = { ...next[i], [field]: val };
        set('identifiers', next);
    };

    const setConcactRow = (i, field, val) => {
        const rows = (cfg.link && cfg.link.url_build && cfg.link.url_build.concact_with || []).map((r) => ({ column: r.value || '', id: r.type || '' }));
        rows[i] = { ...rows[i], [field]: val };
        set('link', {
            ...cfg.link,
            url_build: {
                ...(cfg.link.url_build || {}),
                concact_with: rows.map((r) => ({ type: r.id || 'column', value: r.column })),
            },
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const cleanIdentifiers = cfg.identifiers.filter((r) => r.column || r.id);
        const cleanColumns = cfg.columns.filter(Boolean);
        const payloadCfg = {
            file_type: cfg.file_type || undefined,
            init_row: cfg.init_row === '' ? undefined : cfg.init_row,
            columns: cleanColumns,
            identifiers: cleanIdentifiers,
            status: {
                column: cfg.status.column || undefined,
                method: cfg.status.method || undefined,
                on: cfg.status.on,
                off: cfg.status.off,
            },
            distribution: cfg.distribution || undefined,
        };
        if (cfg.metadata.title.column || cfg.metadata.imprint.column) {
            payloadCfg.metadata = {
                title: cfg.metadata.title.column ? { column: cfg.metadata.title.column } : undefined,
                imprint: cfg.metadata.imprint.column ? { column: cfg.metadata.imprint.column } : undefined,
            };
        }
        const linkMethod = cfg.link.method || '';
        if (linkMethod === 'build') {
            payloadCfg.link = {
                method: 'build',
                url_build: {
                    base_uri: (cfg.link.url_build && cfg.link.url_build.base_uri) || '',
                    concact_with: (cfg.link.url_build && cfg.link.url_build.concact_with || []).filter((r) => r.value),
                },
            };
        } else if (linkMethod === 'get') {
            payloadCfg.link = { method: 'get', column: cfg.link.column || '' };
        }
        // Omitir claves vacías para no pisar valores existentes con undefined/''.
        Object.keys(payloadCfg).forEach((k) => {
            if (payloadCfg[k] === undefined || payloadCfg[k] === '' || (Array.isArray(payloadCfg[k]) && payloadCfg[k].length === 0)) {
                delete payloadCfg[k];
            }
        });
        if (payloadCfg.status) {
            Object.keys(payloadCfg.status).forEach((k) => {
                if (payloadCfg.status[k] === undefined || (Array.isArray(payloadCfg.status[k]) && payloadCfg.status[k].length === 0)) {
                    delete payloadCfg.status[k];
                }
            });
            if (Object.keys(payloadCfg.status).length === 0) delete payloadCfg.status;
        }
        onSubmit({ config_report: payloadCfg });
    };

    const linkMethod = cfg.link && cfg.link.method ? cfg.link.method : '';
    const concactRows = (cfg.link && cfg.link.url_build && cfg.link.url_build.concact_with || []).map((r) => ({ column: r.value || '', id: r.type || '' }));

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <div className="row g-3">
                <div className="col-md-4">
                    <label className="form-label text-white-50">Tipo de archivo</label>
                    <select
                        className="form-select bg-dark text-white border-secondary py-2"
                        value={cfg.file_type}
                        onChange={(e) => set('file_type', e.target.value)}
                    >
                        {FILE_TYPES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Fila inicial (init_row)</label>
                    <input
                        type="number"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={cfg.init_row === '' ? '' : cfg.init_row}
                        onChange={(e) => set('init_row', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Distribución</label>
                    <select
                        className="form-select bg-dark text-white border-secondary py-2"
                        value={cfg.distribution}
                        onChange={(e) => set('distribution', e.target.value)}
                    >
                        {DISTRIBUTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                </div>

                <div className="col-md-6">
                    <ColumnasAdder
                        columns={cfg.columns}
                        onChange={(v) => set('columns', v)}
                    />
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Identificadores (columna → id)</label>
                    {cfg.identifiers.map((row, i) => (
                        <div className="d-flex gap-2 mb-2" key={i}>
                            <div className="flex-grow-1">
                                <ColumnSelect
                                    columns={cfg.columns}
                                    value={row.column}
                                    onChange={(v) => setIdentRow(i, 'column', v)}
                                />
                            </div>
                            <input
                                type="text"
                                className="form-control bg-dark text-white border-secondary py-2 flex-grow-1"
                                placeholder="id (ej. id_ink, isbn_13)"
                                value={row.id}
                                onChange={(e) => setIdentRow(i, 'id', e.target.value)}
                            />
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm flex-shrink-0"
                                onClick={() => set('identifiers', cfg.identifiers.filter((_, x) => x !== i))}
                                title="Quitar"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => set('identifiers', [...cfg.identifiers, {}])}
                        disabled={!cfg.columns.length}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Añadir identificador
                    </button>
                    {cfg.identifiers.length === 0 && <p className="text-white-50 small mt-2 mb-0">Añade las columnas del reporte y luego los identificadores.</p>}
                </div>

                <div className="col-12">
                    <div className="rounded-3 border border-secondary border-opacity-25 p-3">
                        <h6 className="text-white mb-2">Estado (status)</h6>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Columna</label>
                                <ColumnSelect
                                    columns={cfg.columns}
                                    value={cfg.status.column}
                                    onChange={(v) => set('status', { ...cfg.status, column: v })}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-white-50">Método</label>
                                <select
                                    className="form-select bg-dark text-white border-secondary py-2"
                                    value={cfg.status.method}
                                    onChange={(e) => set('status', { ...cfg.status, method: e.target.value })}
                                >
                                    {STATUS_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <CommaMini label="Activo (on)" value={cfg.status.on} onChange={(v) => set('status', { ...cfg.status, on: v })} placeholder="ej. available, Live" />
                            </div>
                            <div className="col-md-3">
                                <CommaMini label="Inactivo (off)" value={cfg.status.off} onChange={(v) => set('status', { ...cfg.status, off: v })} placeholder="ej. unavailable" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="rounded-3 border border-secondary border-opacity-25 p-3">
                        <h6 className="text-white mb-2">Enlace (link)</h6>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label text-white-50">Método</label>
                                <select
                                    className="form-select bg-dark text-white border-secondary py-2"
                                    value={linkMethod}
                                    onChange={(e) => set('link', { method: e.target.value })}
                                >
                                    <option value="">Sin enlace</option>
                                    {LINK_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            {linkMethod === 'get' && (
                                <div className="col-md-5">
                                    <label className="form-label text-white-50">Columna del URL</label>
                                    <ColumnSelect
                                        columns={cfg.columns}
                                        value={cfg.link.column}
                                        onChange={(v) => set('link', { ...cfg.link, column: v })}
                                    />
                                </div>
                            )}
                            {linkMethod === 'build' && (
                                <div className="col-md-9">
                                    <div className="row g-3">
                                        <div className="col-md-12">
                                            <label className="form-label text-white-50">Base URL (base_uri)</label>
                                            <input
                                                type="text"
                                                className="form-control bg-dark text-white border-secondary py-2"
                                                value={(cfg.link.url_build && cfg.link.url_build.base_uri) || ''}
                                                onChange={(v) => set('link', { ...cfg.link, url_build: { ...(cfg.link.url_build || {}), base_uri: v.target.value } })}
                                                placeholder="ej. https://www.storytel.com/es/es/buscar-"
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label text-white-50">Componentes del URL (tipo → columna)</label>
                                            {concactRows.map((row, i) => (
                                                <div className="d-flex gap-2 mb-2" key={i}>
                                                    <input
                                                        type="text"
                                                        className="form-control bg-dark text-white border-secondary py-2 flex-grow-1"
                                                        placeholder="Tipo (ej. column)"
                                                        value={row.id}
                                                        onChange={(e) => setConcactRow(i, 'id', e.target.value)}
                                                    />
                                                    <div className="flex-grow-1">
                                                        <ColumnSelect
                                                            columns={cfg.columns}
                                                            value={row.column}
                                                            onChange={(v) => setConcactRow(i, 'column', v)}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm flex-shrink-0"
                                                        onClick={() => {
                                                            const rows = concactRows.filter((_, x) => x !== i);
                                                            set('link', {
                                                                ...cfg.link,
                                                                url_build: {
                                                                    ...(cfg.link.url_build || {}),
                                                                    concact_with: rows.map((r) => ({ type: r.id || 'column', value: r.column })),
                                                                },
                                                            });
                                                        }}
                                                        title="Quitar"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => {
                                                    const rows = [...concactRows, {}];
                                                    set('link', {
                                                        ...cfg.link,
                                                        url_build: {
                                                            ...(cfg.link.url_build || {}),
                                                            concact_with: rows.map((r) => ({ type: r.id || 'column', value: r.column })),
                                                        },
                                                    });
                                                }}
                                                disabled={!cfg.columns.length}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-1" /> Añadir componente
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="rounded-3 border border-secondary border-opacity-25 p-3">
                        <h6 className="text-white mb-2">Metadatos</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label text-white-50">Columna de título</label>
                                <ColumnSelect
                                    columns={cfg.columns}
                                    value={cfg.metadata.title.column}
                                    onChange={(v) => set('metadata', { ...cfg.metadata, title: { column: v } })}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label text-white-50">Columna de sello (imprint)</label>
                                <ColumnSelect
                                    columns={cfg.columns}
                                    value={cfg.metadata.imprint.column}
                                    onChange={(v) => set('metadata', { ...cfg.metadata, imprint: { column: v } })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-end mt-4 pt-3 border-top border-secondary border-opacity-25">
                <button type="button" className="btn btn-outline-secondary px-4 me-2" onClick={onCancel} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-success px-4" disabled={saving}>
                    {saving ? <FontAwesomeIcon icon={faSpinner} spin className="me-1" /> : null}
                    Guardar reporte
                </button>
            </div>
        </form>
    );
}

export default ReportsChannelForm;